import React, {useState} from 'react'
import {
  AuthButtonGroup,
  Error,
  P,
  AuthLargeHeader,
  AuthMessage,
  AuthLogo
} from '../../components/auth/layout'
import {FormRow} from '../../components/form'
import {Button} from '../../components/library/buttons/index'
import {TextInput} from '../../components/library/form/text'
import {useAuth} from '../../lib/auth'
import {CognitoError} from '../../lib/cognito'

interface ConfirmAccountFormProps {
  username: string
  onComplete: () => void
}

export const ConfirmAccountForm = ({
  username,
  onComplete
}: ConfirmAccountFormProps) => {
  const {auth} = useAuth()
  const [code, setCode] = useState<string>('')
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Remove error if one is already present
    setError('')
    setLoading(true)
    auth
      .confirm(username, code)
      .then(onComplete)
      .catch(err => {
        if (err instanceof CognitoError) {
          if (err.message.includes('Invalid verification code')) {
            setError('Incorrect confirmation code')
          } else if (err.message.includes('Attempt limit exceeded')) {
            setError('Too many incorrect attempts. Try again later.')
          } else {
            setError(err.message)
          }
        } else {
          throw err
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const resendCode = () => {
    // Remove error if one is already present
    setError('')
    setSending(true)
    auth
      .resendCode(username)
      .catch(err => {
        if (err instanceof CognitoError) {
          if (err.message.includes('Attempt limit exceeded')) {
            setError('Code reset too many times. Try again later.')
          } else {
            setError(err.message)
          }
        } else {
          throw err
        }
      })
      .finally(() => {
        setSending(false)
      })
  }

  return (
    <>
      <AuthLogo />
      <AuthLargeHeader>Confirm account</AuthLargeHeader>
      <AuthMessage>
        Enter the confirmation code you received via email to confirm the
        account for {username}
      </AuthMessage>

      <form onSubmit={onSubmit}>
        {error ? <Error>{error}</Error> : null}

        <FormRow>
          <TextInput
            type="text"
            label="Enter code..."
            upLabel="Code"
            required={true}
            onChange={setCode}
            value={code ? code : ''}
            style={{maxWidth: 'none'}}
          />
        </FormRow>

        <AuthButtonGroup>
          <Button
            type="submit"
            appearance="primary"
            size="large"
            disabled={loading}
            style={{
              width: '100%'
            }}
          >
            {loading ? 'Loading...' : 'Confirm account'}
          </Button>
        </AuthButtonGroup>
        <P>
          <Button size="large" appearance="transparent" onClick={resendCode}>
            {sending ? 'Sending...' : 'Resend code'}
          </Button>
        </P>
      </form>
    </>
  )
}
